/**
 * PBI #299: builds the map Dropdown renders as a muted second line under an account type,
 * from the agreement's account types.
 *
 * Dropdown's options are plain strings, so the map can only be keyed on the account type
 * name. Two account types in one agreement may share a name, so the entry for the name of
 * the account type in use is replaced with that type's own description, resolved by public
 * id. The selected type's description therefore wins over a same-named sibling's, whatever
 * the order of the list.
 *
 * A missing, null or blank customerDescription yields no entry at all, so nothing is
 * rendered rather than the text "null".
 *
 * @param {Array<{accountTypePublicId?: string, name?: string, customerDescription?: string}>} accountTypes
 * @param {string} selectedAccountTypePublicId public id of the account type in use
 * @returns {Object<string, string>} option string -> description
 */
export const buildAccountTypeDescriptions = (
  accountTypes = [],
  selectedAccountTypePublicId = ""
) => {
  const list = Array.isArray(accountTypes) ? accountTypes : [];

  const descriptions = list.reduce((map, accountType) => {
    if (!accountType?.name || !accountType?.customerDescription) return map;
    return { ...map, [accountType.name]: accountType.customerDescription };
  }, {});

  const selected = list.find(
    (accountType) =>
      !!accountType?.accountTypePublicId &&
      accountType.accountTypePublicId === selectedAccountTypePublicId
  );

  if (!selected?.name) return descriptions;

  // Whatever a same-named sibling contributed for this name is dropped, so the selected
  // account type's own description (or its absence) is what shows.
  const withoutSelectedName = Object.fromEntries(
    Object.entries(descriptions).filter(([name]) => name !== selected.name)
  );

  return selected.customerDescription
    ? { ...withoutSelectedName, [selected.name]: selected.customerDescription }
    : withoutSelectedName;
};
